// Call diagnostics "Routing": the append-only routing decisions of one inbound call
// (GET /api/v1/routing/decisions?call_id=). Pure types, parsing, grouping and copy.
// Deliberately separate from the admin model: the call detail loads this with the main bundle.
//
// Routing data says who was rung, what each ring step returned and who answered. It never says who
// hung up; that stays with the call's own attribution in "What happened".
import type * as C from '@bcio-voice/contracts';
import { FLOW_REASON_SENTENCE, type FlowEntry, readFlowEntries } from '../flow/entries.ts';

const KINDS = ['rule_matched', 'legacy_route', 'redirected', 'ring_step_started', 'ring_step_ended', 'fallback', 'final'] as const;
// 'voicemail' (migration 015): the API reads COALESCE(final_outcome, outcome), so a voicemail call's final row says voicemail.
const OUTCOMES = ['answered', 'no_answer', 'busy', 'unavailable', 'rejected', 'cancelled', 'answered_by_user', 'missed', 'redirected', 'voicemail'] as const;
const FINALS = ['answered_by_user', 'missed', 'busy', 'unavailable', 'voicemail'] as const;
const STATES = ['open', 'closed', 'holiday'] as const;
const REASONS = ['weekly', 'special_open', 'holiday', 'no_window'] as const;
// 'ivr_menu' (migration 016): a rule's open or closed target may be a phone menu.
const TARGET_KINDS = ['user', 'ring_group', 'internal_number', 'missed_call', 'voicemail', 'ivr_menu'] as const;

// The contract's Static types widen these code lists to string; readEntry checks every value.
export type DecisionKind = (typeof KINDS)[number];
export type DecisionOutcome = (typeof OUTCOMES)[number];
export type FinalOutcome = (typeof FINALS)[number];
type Target = { kind: (typeof TARGET_KINDS)[number]; id?: string };
export interface DecisionEntry extends Omit<C.RoutingDecisionEntry, 'kind' | 'outcome' | 'schedule_state' | 'schedule_reason' | 'target'> {
  kind: DecisionKind;
  outcome: DecisionOutcome | null;
  schedule_state: (typeof STATES)[number] | null;
  schedule_reason: (typeof REASONS)[number] | null;
  target: Target | null;
}

/** Permissions under which the call itself (and so its routing) may be visible. The server decides. */
export const DECISION_PERMISSIONS = ['calls:read_own', 'calls:read_team', 'calls:read_all'] as const;

export const ROUTING_PROVENANCE = 'Routing decisions are written by the server while it routes the call. They show who was rung and who answered, never who hung up.';

/** Under "Went to voicemail": routing decides where the call goes, not whether a message was left. */
export const VOICEMAIL_FINAL_NOTE = 'Nobody answered in person. A message appears in Voicemail only if the caller left one of at least one second.';

export interface DecisionLog {
  callId: string;
  finalOutcome: FinalOutcome | null;
  answeredBy: string | null;
  entries: DecisionEntry[];
  /** Queue and phone-menu rows (`flow_entries`, migration 016), in sequence order; none from an API before 016. */
  flow: FlowEntry[];
  /** Entries this version couldn't read. */
  skipped: number;
}

const ID = /^[A-Za-z0-9_.:-]{1,128}$/;
const CODE = /^[a-z][a-z0-9_]{0,59}$/;
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function oneOf<T extends string>(list: readonly T[], value: unknown): T | null {
  return typeof value === 'string' && (list as readonly string[]).includes(value) ? (value as T) : null;
}
const id = (value: unknown): string | null => (typeof value === 'string' && ID.test(value) ? value : null);
const time = (value: unknown): string | null => (typeof value === 'string' && Number.isFinite(Date.parse(value)) ? value : null);
const ids = (value: unknown): string[] => (Array.isArray(value) ? value.map(id).filter((x): x is string => x !== null) : []);

function readTarget(value: unknown): Target | null {
  if (!isRecord(value)) return null;
  const kind = oneOf(TARGET_KINDS, value.kind);
  if (!kind) return null;
  const target = id(value.id);
  return target ? { kind, id: target } : { kind };
}

export function readEntry(value: unknown): DecisionEntry | null {
  if (!isRecord(value)) return null;
  const kind = oneOf(KINDS, value.kind), entryId = id(value.id), at = time(value.at);
  const sequence = typeof value.sequence === 'number' && Number.isInteger(value.sequence) && value.sequence >= 1 ? value.sequence : null;
  if (!kind || !entryId || !at || sequence === null) return null;
  const step = typeof value.step === 'number' && Number.isInteger(value.step) && value.step >= 1 ? value.step : null;
  if ((kind === 'ring_step_started' || kind === 'ring_step_ended') && step === null) return null;
  return {
    id: entryId, sequence, at, kind,
    number_id: id(value.number_id), rule_id: id(value.rule_id),
    rule_version: typeof value.rule_version === 'number' && Number.isInteger(value.rule_version) && value.rule_version >= 1 ? value.rule_version : null,
    schedule_state: oneOf(STATES, value.schedule_state), schedule_reason: oneOf(REASONS, value.schedule_reason),
    target: readTarget(value.target), step, user_ids: ids(value.user_ids).slice(0, 50), leg_ids: ids(value.leg_ids).slice(0, 500),
    outcome: oneOf(OUTCOMES, value.outcome), reason: typeof value.reason === 'string' && CODE.test(value.reason) ? value.reason : null,
    answered_by_user_id: id(value.answered_by_user_id), started_at: time(value.started_at), ended_at: time(value.ended_at),
  };
}

/** The decision log, or null when the response isn't one. Entries are put in sequence order. */
export function readDecisionLog(value: unknown): DecisionLog | null {
  if (!isRecord(value) || !Array.isArray(value.entries)) return null;
  const callId = id(value.call_id);
  if (!callId) return null;
  const entries: DecisionEntry[] = [];
  let skipped = 0;
  for (const raw of value.entries.slice(0, 500)) {
    const entry = readEntry(raw);
    if (entry) entries.push(entry);
    else skipped += 1;
  }
  skipped += Math.max(0, value.entries.length - 500);
  entries.sort((a, b) => a.sequence - b.sequence);
  const flow = readFlowEntries(value.flow_entries);
  flow.entries.sort((a, b) => a.sequence - b.sequence);
  return { callId, finalOutcome: oneOf(FINALS, value.final_outcome), answeredBy: id(value.answered_by_user_id), entries, flow: flow.entries, skipped: skipped + flow.skipped };
}

// ---- Grouping -------------------------------------------------------------------------------------------

export type DecisionItem =
  | { kind: 'entry'; entry: DecisionEntry }
  | { kind: 'step'; step: number; started: DecisionEntry | null; ended: DecisionEntry | null }
  | { kind: 'flow'; entry: FlowEntry };

/**
 * One item per decision, except that a ring step's start and end become one item, at the start's place. Queue and menu
 * rows share the decisions' sequence, so they interleave where the server made them.
 */
export function decisionItems(entries: readonly DecisionEntry[], flow: readonly FlowEntry[] = []): DecisionItem[] {
  const items: DecisionItem[] = [];
  const steps = new Map<number, Extract<DecisionItem, { kind: 'step' }>>();
  const merged: ({ flow: false; entry: DecisionEntry } | { flow: true; entry: FlowEntry })[] = [
    ...entries.map(entry => ({ flow: false as const, entry })),
    ...flow.map(entry => ({ flow: true as const, entry })),
  ].sort((a, b) => a.entry.sequence - b.entry.sequence);
  for (const row of merged) {
    if (row.flow) {
      items.push({ kind: 'flow', entry: row.entry });
      continue;
    }
    const entry = row.entry;
    if ((entry.kind === 'ring_step_started' || entry.kind === 'ring_step_ended') && entry.step !== null) {
      let item = steps.get(entry.step);
      if (!item) {
        item = { kind: 'step', step: entry.step, started: null, ended: null };
        steps.set(entry.step, item);
        items.push(item);
      }
      if (entry.kind === 'ring_step_started' && !item.started) item.started = entry;
      else if (entry.kind === 'ring_step_ended' && !item.ended) item.ended = entry;
      continue;
    }
    items.push({ kind: 'entry', entry });
  }
  return items;
}

// ---- Copy -------------------------------------------------------------------------------------------------

export interface DecisionNames {
  person: (userId: string) => string | null;
  number: (numberId: string) => { label: string; e164: string } | null;
}

const personOf = (names: DecisionNames, userId: string | null | undefined) => (userId ? names.person(userId) ?? 'a teammate' : 'a teammate');
const numberOf = (names: DecisionNames, numberId: string | null | undefined) => {
  const n = numberId ? names.number(numberId) : null;
  return n ? (n.label ? `${n.label} (${n.e164})` : n.e164) : 'another of our numbers';
};

export function targetPhrase(target: Target | null, names: DecisionNames): string {
  if (!target) return 'no target';
  switch (target.kind) {
    case 'user':
      return personOf(names, target.id);
    case 'ring_group':
      return 'a ring group';
    case 'internal_number':
      return numberOf(names, target.id);
    case 'missed_call':
      return 'a missed call (nothing rings)';
    case 'voicemail':
      return 'voicemail';
    case 'ivr_menu':
      return 'a phone menu';
  }
}

export const SCHEDULE_WORD = { open: 'open', closed: 'closed', holiday: 'closed for a holiday' } as const;
const SCHEDULE_REASON_WORD = { weekly: 'inside the weekly hours', special_open: 'special opening hours for the date', holiday: 'a holiday', no_window: 'outside the weekly hours' } as const;

export const STEP_OUTCOME_LABEL: Record<DecisionOutcome, string> = {
  answered: 'Answered',
  no_answer: 'No answer',
  busy: 'Busy on another call',
  unavailable: 'Unavailable: no phone online or do not disturb',
  rejected: 'Declined on the phone',
  cancelled: 'Stopped before it finished',
  answered_by_user: 'Answered',
  missed: 'Missed',
  redirected: 'Redirected',
  voicemail: 'Went to voicemail',
};

/** Reason codes the controller writes, in plain words. None of them names who hung up. */
export const REASON_SENTENCE: Record<string, string> = {
  no_answer: 'Someone was rung and nobody answered.',
  closed_missed_call: 'The schedule was closed and the rule logs a missed call.',
  voicemail_unavailable: 'The rule sends calls to voicemail, but the server’s voicemail service wasn’t running, so the call was logged as missed.',
  voicemail_greeting_required: 'The rule sends calls to voicemail, but the server requires a greeting and this number has none, so the call was logged as missed.',
  closed_voicemail: 'The schedule was closed and the rule sends calls to voicemail.',
  no_answer_voicemail: 'Someone was rung and nobody answered, so the call went to voicemail.',
  busy_voicemail: 'Everyone who could take the call was on another call, so it went to voicemail.',
  unavailable_voicemail: 'Nobody could be rung, so the call went to voicemail.',
  busy: 'Everyone who could take the call was on another call.',
  unavailable: 'Nobody could be rung: no phone was online, or do not disturb was on.',
  caller_abandoned: 'The call ended before anyone answered. “What happened” above shows how it ended.',
  redirect_limit: 'The call had already been redirected once, so a second redirect wasn’t followed.',
  redirect_target_unrouted: 'The number it was redirected to has no rule and no assigned user.',
  call_failed: 'The call failed while it was being routed.',
  duration_limit: 'The workspace’s time limit for calls ended routing.',
  call_ended: 'The call ended while routing was still in progress.',
  busy_target: 'Everyone was busy, so the rule’s busy fallback ran.',
  unavailable_target: 'Nobody could be reached, so the rule’s unavailable fallback ran.',
  internal_number: 'The rule sends calls to another of our numbers.',
  ...FLOW_REASON_SENTENCE,
};

export function finalHeadline(outcome: FinalOutcome | null, answeredBy: string | null, names: DecisionNames): string {
  switch (outcome) {
    case 'answered_by_user':
      return `Answered by ${personOf(names, answeredBy)}`;
    case 'missed':
      return 'Missed: nobody answered';
    case 'busy':
      return 'Busy: everyone who could take it was on another call';
    case 'unavailable':
      return 'Unavailable: nobody could be reached';
    case 'voicemail':
      return 'Went to voicemail';
    default:
      return 'Routing hasn’t finished';
  }
}

/** One sentence for a non-step decision. */
export function entrySentence(e: DecisionEntry, names: DecisionNames): string {
  switch (e.kind) {
    case 'rule_matched': {
      const version = e.rule_version ? ` (version ${e.rule_version})` : '';
      const schedule = e.schedule_state ? ` The schedule was ${SCHEDULE_WORD[e.schedule_state]}${e.schedule_reason && e.schedule_state !== 'holiday' ? `, ${SCHEDULE_REASON_WORD[e.schedule_reason]}` : ''}.` : '';
      return `A routing rule${version} matched${e.number_id ? ` on ${numberOf(names, e.number_id)}` : ''}.${schedule}${e.target ? ` It sends the call to ${targetPhrase(e.target, names)}.` : ''}`;
    }
    case 'legacy_route':
      return `${e.number_id ? `${numberOf(names, e.number_id)} has` : 'The number has'} no enabled routing rule, so its single assigned target applied.`;
    case 'redirected':
      return `Redirected to ${targetPhrase(e.target, names)}. That number’s own rule decides next.`;
    case 'fallback': {
      const why = e.outcome === 'busy' ? 'Everyone was busy' : e.outcome === 'unavailable' ? 'Nobody could be reached' : 'Nobody could be rung';
      return `${why}, so the fallback ran: ${targetPhrase(e.target, names)}.`;
    }
    case 'final':
      return finalHeadline(e.outcome && (FINALS as readonly string[]).includes(e.outcome) ? (e.outcome as FinalOutcome) : null, e.answered_by_user_id, names) + '.';
    default:
      return 'Routing step.';
  }
}

export function stepSentence(started: DecisionEntry | null, ended: DecisionEntry | null, step: number, names: DecisionNames): string {
  const users = (started ?? ended)?.user_ids ?? [];
  const who = users.length === 0 ? 'nobody' : users.length <= 4 ? users.map(u => personOf(names, u)).join(', ') : `${users.slice(0, 3).map(u => personOf(names, u)).join(', ')} and ${users.length - 3} more`;
  const via = started?.target?.kind === 'ring_group' ? ' (ring group)' : '';
  return `Ring step ${step}: rang ${who}${via}.`;
}

/** "18 s" between two instants, or null. */
export function stepDuration(started: string | null, ended: string | null): string | null {
  if (!started || !ended) return null;
  const ms = Date.parse(ended) - Date.parse(started);
  if (!Number.isFinite(ms) || ms < 0) return null;
  const s = Math.round(ms / 1000);
  return s < 60 ? `${s} s` : `${Math.floor(s / 60)} min ${s % 60} s`;
}

// ---- View state ---------------------------------------------------------------------------------------------

export type DecisionsView =
  | { state: 'denied'; code: string | null; requestId: string | null }
  | { state: 'none'; requestId: string | null }
  | { state: 'loading' }
  | { state: 'access'; access: 'refreshing' | 'blocked' }
  | { state: 'error'; error: { title: string; code: string; requestId: string | null } }
  | { state: 'empty' }
  | { state: 'ready'; log: DecisionLog };

export interface DecisionsInput {
  canRead: boolean;
  status: 'idle' | 'loading' | 'ready' | 'error' | 'refreshing' | 'blocked';
  value: { data?: unknown } | null;
  error: { title: string; code: string; requestId: string | null; status?: number } | null;
}

/** Exactly one state for the panel. A 404 means the server has no routing decisions this person may see. */
export function decisionsView(input: DecisionsInput): DecisionsView {
  if (!input.canRead) return { state: 'denied', code: null, requestId: null };
  if (input.status === 'refreshing' || input.status === 'blocked') return { state: 'access', access: input.status };
  if (input.status === 'error' && input.error) {
    if (input.error.status === 403 && input.error.code !== 'mfa_required') return { state: 'denied', code: input.error.code, requestId: input.error.requestId };
    if (input.error.status === 404) return { state: 'none', requestId: input.error.requestId };
    return { state: 'error', error: input.error };
  }
  if (input.status !== 'ready' || !input.value) return { state: 'loading' };
  const log = readDecisionLog(input.value.data);
  if (!log) return { state: 'error', error: { title: 'The server sent routing decisions this version can’t read', code: 'invalid_response', requestId: null } };
  if (log.entries.length === 0 && log.flow.length === 0 && log.skipped === 0) return { state: 'empty' };
  return { state: 'ready', log };
}
